// Keep the visible default time and the scheduler fields in the same payload.
// The API schedules from `hour` and `minute`, while the time input displays `time`.
export const buildDefaultNotification = (idClient) => ({
    name: "",
    id_client: idClient,
    type: "invoice_volume",
    frequency: {
        type: "monthly",
        interval: {
            hour: 9,
            minute: 0,
            second: 0,
            day_of_the_month: 1,
            time: "09:00",
            timezone: "America/Toronto",
        },
    },
    recipients: [],
    description: "",
    active: true,
});

// Notification types the API fires from an event rather than a timer, and whose
// audience it resolves itself. Kept here rather than in the edit form because
// both the form (to hide the schedule and recipient inputs) and the admin page
// (to strip those fields before saving) need the same answer.
//
// Maintained by hand alongside the API's own EVENT_DRIVEN_TYPES in
// xpens-api notifications_service.py. Adding a type there and not here brings
// back a schedule picker that does nothing.
export const EVENT_DRIVEN_NOTIFICATION_TYPES = [
    "plaid_missing_receipt_alert",
    "expense_item_denied",
];

export const isEventDrivenNotificationType = (type) =>
    EVENT_DRIVEN_NOTIFICATION_TYPES.includes(type);

/**
 * Strips the fields an event-driven notification must never carry.
 *
 * The create form starts from buildDefaultNotification, which always carries a
 * schedule, and a notification switched over from another type still holds the
 * addresses typed for it. Neither belongs on the saved document. The API works
 * out each event-driven type's audience itself -- the card owners for the
 * missing-receipt alert; the expense owner and, if chosen, their assistants for
 * the refused-expense email -- and fires it from an event (a webhook, a
 * refusal), never from a timer. The admin list prints whatever frequency it
 * finds, so a seeded "monthly" would show such a notification as running on a
 * schedule, permanently.
 *
 * Limit worth knowing: the API updates with $set, which merges. Omitting a key
 * keeps a NEW event-driven notification clean, but it cannot remove a value an
 * existing document already has. A notification converted from a scheduled type
 * therefore keeps its old frequency, recipients and job_id server-side -- that
 * needs an $unset and an unschedule on the API, tracked separately.
 *
 * @param {object} notification - The notification about to be sent to the API.
 * @returns {object} A copy without `frequency`/`recipients` when the type is
 *   event-driven; otherwise an equivalent copy, unchanged.
 */
export const withoutScheduleIfEventDriven = (notification) => {
    if (!isEventDrivenNotificationType(notification?.type)) {
        return {...notification};
    }
    const {frequency, recipients, ...rest} = notification;
    return rest;
};
